/**
 * The own-scope overlap wording, in one place (Story #5648 build v2, figure
 * S4b). Separate from the components so the sentences can be tested directly
 * and so a component file exports only components.
 *
 * The vocabulary difference from the aggregator view is deliberate and is the
 * point: at own scope every member is an INVENTION of yours, never "a project"
 * belonging to somebody in a portfolio.
 */
import type { OverlapFundingRollup, OverlapGroup } from '../../types/whitespace'

/** The figure's anchor sentence. The number is the served score; the sentence
 * is computed from it, so the two can never disagree. */
export function ownStrongestPairAnchor(score: number): string {
  return `Strongest pair: ${score} — the share of the smaller invention's documents with a match in the other`
}

export function ownGroupMetaLine(group: OverlapGroup): string {
  const n = group.member_project_ids.length
  return `${n} ${n === 1 ? 'invention' : 'inventions'} · ${ownStrongestPairAnchor(group.score)}`
}

/** Programmes named by more than one of the caller's inventions. A row with a
 * single mentioning project is not a duplicate and is never counted as one. */
export function duplicateFundingRows(rollup: OverlapFundingRollup | undefined) {
  return (rollup?.funding_mentions_rows ?? []).filter(
    (r) => new Set(r.mentions.map((m) => m.project_id)).size > 1,
  )
}

/**
 * The rail header's coverage line (review round 2, S4b item 3).
 *
 * "compared 6 of 10" read as a contradiction: the sidebar badge, the header
 * chip, the focus-rail caption and the Shape tile all say NINE inventions, and
 * nothing on the page explained the tenth. It is the one project shared WITH the
 * caller — the overlap run compares everything the caller is entitled to see,
 * which is their own inventions plus what has been shared with them.
 *
 * So the line now says that, in its own words, from the real counts. The
 * breakdown is printed ONLY when it actually adds up to the served denominator;
 * when it does not, the honest generic clause is used instead of asserting a
 * split the numbers do not support. A corrected number with no reason would be
 * worse than the contradiction it replaces.
 */
export function comparedCoverageLine(input: {
  comparedCount?: number
  memberCount?: number
  ownedCount: number
  sharedWithYouCount: number
}): string {
  const { comparedCount, memberCount, ownedCount, sharedWithYouCount } = input
  if (comparedCount === undefined) {
    // The run did not record how many it compared. Say where the groups came
    // from rather than print a number nobody measured.
    return 'Groups · from your last overlap scan'
  }
  // THE DENOMINATOR IS THE CALLER'S OWN POOL (review round 9, item 3). It used
  // to be the run's member count, which on this account is 10 — nine of bob's
  // own inventions plus one shared with him — and the extra one needed a clause
  // to explain it, which is what pushed the line onto a second row and
  // eventually got the whole count deleted. On a screen whose scope IS "your
  // inventions", "of 9" is both shorter and the number the reader is holding.
  const head = `Groups · compared ${comparedCount} of ${ownedCount}`
  if (memberCount !== undefined && memberCount !== ownedCount + sharedWithYouCount) {
    // The run's universe is not the one this screen describes. Say so rather
    // than let a number stand that quietly means something else.
    return `${head} of yours · the scan covered ${memberCount}`
  }
  return head
}
